/**
 * EPL Cart Drawer - Standalone implementation with EPL-specific functionality
 * Handles AppleCare, Second Warranty, recommended accessories, and EPL-specific UI updates
 * EPL: Independent from cart-drawer.js - all base functionality copied from CartDrawer
 */

// Prevent multiple script executions
(function() {
  'use strict';
  
  if (window.__EPL_CART_DRAWER_LOADED__) {
    return; // Script already loaded, skip execution
  }
  window.__EPL_CART_DRAWER_LOADED__ = true;

  class CartDrawerEpl extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }
      this.setHeaderCartIconAccessibility();
      this.addEvents = this.addEvents.bind(this);

      // Lock mechanism to prevent concurrent operations (both add and remove)
      this._operationLock = false;
      this._pendingOperations = [];

      // Lock mechanism to prevent concurrent updateItems calls
      this._updateLock = false;
      this._pendingUpdate = null;

      // True from the optimistic drawer open until the ATC add commits (or fails).
      // Gates the footer "Vis handlekurv" nav link and blocks re-entrant adds so the
      // user can't navigate to / re-add against a cart that hasn't committed yet.
      this._addPending = false;
      this._addPendingTimer = null;

      this.addEvents();
      this.setupAddonPickerBridge();
      this._bindNavGate();
      this._bindCartCountSync();
    }

    /**
     * Updates (or creates) the header cart-count bubble. Single source of truth shared by
     * updateCartCount and the cart:updated listener, so removing the per-ATC minicart fetch
     * doesn't leave the header count stale.
     */
    _updateHeaderCartCount(itemCount) {
      const count = parseInt(itemCount, 10) || 0;
      const cartCountBubble = document.querySelector('.cart-count-bubble span');
      const cartIconBubble = document.querySelector('#cart-icon-bubble');
      if (cartCountBubble) {
        cartCountBubble.textContent = count;
      } else if (cartIconBubble && count > 0) {
        const bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        const span = document.createElement('span');
        span.textContent = count;
        bubble.appendChild(span);
        cartIconBubble.appendChild(bubble);
      }
    }

    /**
     * Keeps the header cart-count bubble in sync with every cart:updated event (ATC, quantity
     * change, remove). This replaces the per-ATC `cart?view=minicart` fetch that previously
     * drove the bubble — the count now rides the cart.js payload already dispatched on commit.
     */
    _bindCartCountSync() {
      if (this._cartCountSyncBound) return;
      this._cartCountSyncBound = true;
      document.addEventListener('cart:updated', (e) => {
        const cart = e.detail;
        if (cart && typeof cart.item_count !== 'undefined') {
          this._updateHeaderCartCount(cart.item_count);
        }
      });
    }

    /**
     * Sets/clears the "an add is in flight" state. Toggles the is-add-pending class
     * (CSS dims + disables the footer link) and runs a watchdog so the gated nav link
     * can never strand permanently if some exit path forgets to clear it.
     */
    _setAddPending(pending) {
      this._addPending = !!pending;
      this.classList.toggle('is-add-pending', this._addPending);
      if (this._addPendingTimer) { clearTimeout(this._addPendingTimer); this._addPendingTimer = null; }
      if (this._addPending) {
        this._addPendingTimer = setTimeout(() => {
          console.warn('Cart add-pending watchdog fired — clearing stuck pending state');
          this._setAddPending(false);
        }, 10000);
      }
    }

    /**
     * Recovers the drawer after a failed ATC add (422 / network error). Clears the pending
     * gate and restores the correct state so the optimistic loading screen never sticks:
     * if the cart is empty, explicitly restore the empty state (the optimistic open already
     * stripped is-empty, so updateItems' wasEmpty-based empty handling wouldn't); otherwise
     * re-sync to the real cart.
     */
    _recoverFromFailedAdd() {
      this._setAddPending(false);
      fetch(`${window.Shopify.routes.root}cart.js`)
        .then((r) => r.json())
        .then((cart) => {
          if (!cart.items || cart.items.length === 0) {
            const $drawerItems = this.querySelector('cart-drawer-items-epl');
            const $emptyDrawer = this.querySelector('.js-empty-drawer');
            this.classList.add('is-empty');
            if ($drawerItems) $drawerItems.classList.add('is-empty');
            if ($emptyDrawer) $emptyDrawer.classList.remove('hidden');
            // Clear any optimistic card so it doesn't linger (hidden) in the items body.
            const $body = this.querySelector('.js-cart-items-body');
            if ($body) $body.innerHTML = '';
          } else {
            this.updateItems(null, null, false);
          }
        })
        .catch(() => { this.updateItems(null, null, false); });
    }

    /**
     * Intercepts clicks on the footer "Vis handlekurv" link while an add is pending and
     * cancels the navigation, so a fast click can't load a /cart page that doesn't yet
     * reflect the just-added item. Capture phase + a single delegated listener; also
     * covers keyboard activation (Enter on an anchor dispatches a click).
     */
    _bindNavGate() {
      if (this._navGateBound) return;
      this._navGateBound = true;
      this.addEventListener('click', (e) => {
        if (!this._addPending) return;
        const link = e.target.closest && e.target.closest('a.button--epl-cart');
        if (link) { e.preventDefault(); e.stopPropagation(); }
      }, true);
    }

    /**
     * Listen for addon-picker:chosen events and route them through
     * handleAddSecondWarranty by building a synthetic button element
     * with the data attributes that handler expects.
     */
    setupAddonPickerBridge() {
      if (this._addonPickerBridgeBound) return;
      this._addonPickerBridgeBound = true;

      document.addEventListener('addon-picker:chosen', (event) => {
        const detail = event.detail || {};
        if (!detail.mainItemKey) return;

        // The cart drawer is rendered in layout/theme.liquid on every page,
        // so this listener fires on pages where the drawer isn't the
        // originating surface (e.g. cart page, where cart-epl.js also
        // listens). Only handle events whose originating trigger lives
        // inside this drawer — otherwise we'd double-add. If the trigger
        // is missing or detached (row re-rendered between open and choose),
        // bail rather than guessing which surface owns the event.
        if (!detail.triggerElement || !this.contains(detail.triggerElement)) return;

        const modalId = 'modal-sw-picker-drawer-' + detail.mainItemKey;
        const button = this.querySelector(
          '[data-addon-picker-trigger="true"][data-modal-target="' + modalId + '"]'
        );
        if (!button) return;

        button.dataset.variantid = detail.variantId || '';
        button.dataset.skusw = detail.productSku || '';
        button.dataset.handlesw = detail.productHandle || '';
        button.dataset.skuparent = detail.mainProductSku || '';
        button.dataset.handleparent = detail.mainProductHandle || '';
        button.dataset.keyparent = detail.mainItemKey;
        button.dataset.uuid = detail.mainProductUuid || detail.mainProductHandle || '';

        // Hard-bundled SW is mandatory: a pick means "swap type", never a
        // fresh add.
        if (button.dataset.swHardBundle === 'true') {
          this.handleSwapSecondWarranty(button);
          return;
        }

        this.handleAddSecondWarranty(button);
      });
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink) {
        return;
      }
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    close() {
      this.classList.remove('active');
      removeTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden');
      document.documentElement.style.overflow = '';
    }

    setSummaryAccessibility(cartDrawerNote) {
      cartDrawerNote.setAttribute('role', 'button');
      cartDrawerNote.setAttribute('aria-expanded', 'false');

      if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
        cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
      }

      cartDrawerNote.addEventListener('click', (event) => {
        event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
      });

      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer-epl',
          selector: '#CartDrawer',
        },
        {
          id: 'cart-icon-bubble',
        },
      ];
    }

    getSectionDOM(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    }

    open(triggeredBy, prepareForUpdate = false, optimisticItem = null) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

      const wasEmpty = this.classList.contains('is-empty');

      // EPL-specific: If prepareForUpdate is true, always prepare loading screen before opening
      // This ensures instant loading feedback when adding products, regardless of cart state.
      // When an optimisticItem is supplied, paint the just-added product immediately instead
      // of a bare spinner (reconciled to the authoritative HTML when the server responds).
      if (prepareForUpdate) {
        this.prepareLoadingScreen(optimisticItem);
      }
      // EPL-specific: Check if content needs to be refreshed (only when NOT preparing for update)
      else if (!wasEmpty) {
        // Check if content is already loaded
        const $items = this.querySelector('.js-cart-items-body');
        const hasContent = $items && $items.querySelector('.epl-cart-summary') &&
                          !$items.querySelector('.epl-cart-summary').textContent.includes('Laster handlekurv');

        // Only refresh if content is missing or we haven't just updated (avoid double refresh)
        if (!hasContent && !this._justUpdated) {
          // Mark that we're about to open, so updateItems won't call open() again
          this._isOpening = true;
          // Fetch content immediately without showing loading screen
          this.updateItems(null, null, false); // Pass false to prevent auto-opening
          this._isOpening = false;
        }
        this._justUpdated = false;
      }
      
      // Base functionality: Add classes immediately for faster opening
      // Use requestAnimationFrame to ensure DOM is ready, but don't delay unnecessarily
      requestAnimationFrame(() => {
        this.classList.add('animate', 'active');
        
        // Constrain cart-drawer-items-epl height to available space
        const drawerInner = this.querySelector('.drawer__inner');
        const drawerItemsEpl = this.querySelector('cart-drawer-items-epl');
        const drawerHeader = this.querySelector('.drawer__header');
        const drawerFooter = this.querySelector('.drawer__footer');
        
        if (drawerInner && drawerItemsEpl) {
          const innerHeight = parseFloat(getComputedStyle(drawerInner).height) || 0;
          const headerHeight = drawerHeader ? (parseFloat(getComputedStyle(drawerHeader).height) || 0) : 0;
          const footerHeight = drawerFooter ? (parseFloat(getComputedStyle(drawerFooter).height) || 0) : 0;
          const availableHeight = innerHeight - headerHeight - footerHeight;
          
          if (availableHeight > 0) {
            const currentHeight = parseFloat(getComputedStyle(drawerItemsEpl).height) || 0;
            if (currentHeight === 0 || currentHeight > availableHeight) {
              drawerItemsEpl.style.maxHeight = `${availableHeight}px`;
              drawerItemsEpl.style.height = `${availableHeight}px`;
              drawerItemsEpl.style.overflowY = 'auto';
            }
          }
        }
      });

      // Base functionality: Set up focus trapping
      this.addEventListener(
        'transitionend',
        () => {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          trapFocus(containerToTrapFocusOn, focusElement);
        },
        { once: true }
      );

      // Base functionality: Prevent body scrolling
      document.body.classList.add('overflow-hidden');
      // Also set overflow on html element to prevent scrolling
      document.documentElement.style.overflow = 'hidden';
    }

    renderContents(parsedState) {
      this.querySelector('.drawer__inner').classList.contains('is-empty') &&
        this.querySelector('.drawer__inner').classList.remove('is-empty');
      this.productId = parsedState.id;
      
      const wasEmpty = this.classList.contains('is-empty');
      
      // EPL-specific: If transitioning from empty to non-empty, immediately hide empty state and show loading
      // Do this BEFORE updating sections to prevent flicker
      if (wasEmpty) {
        const $emptyDrawer = this.querySelector('.js-empty-drawer');
        if ($emptyDrawer) {
          $emptyDrawer.classList.add('hidden');
        }
        
        const $drawerItems = this.querySelector('cart-drawer-items-epl');
        if ($drawerItems) {
          $drawerItems.classList.remove('is-empty');
          this.classList.remove('is-empty');
          const $inner = $drawerItems.querySelector('.js-drawer-inner');
          if ($inner) {
            // Show loading screen immediately to prevent flicker
            $inner.innerHTML = `
              <div class="drawer__cart-items-wrapper js-cart-items-body">
                <div class="epl-cart-summary" style="min-height: 200px;">
                  <div style="text-align: center; padding: 40px; color: #666;">
                    <p>Laster handlekurv...</p>
                  </div>
                </div>
              </div>
            `;
          }
        }
      }
      
      // Base functionality: Update sections via getSectionsToRender
      // EPL: Only update if the section exists in parsedState to avoid replacing the entire drawer
      this.getSectionsToRender().forEach((section) => {
        // Skip if section doesn't exist in parsedState (prevents replacing footer)
        if (!parsedState.sections || !parsedState.sections[section.id]) {
          return;
        }
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (sectionElement) {
          // For cart-drawer-epl section, only update the cart items, not the entire drawer
          if (section.id === 'cart-drawer-epl') {
            const cartItemsContainer = sectionElement.querySelector('.js-contents') || sectionElement.querySelector('.js-drawer-inner');
            if (cartItemsContainer) {
              const sectionHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
              const parser = new DOMParser();
              const doc = parser.parseFromString(sectionHTML, 'text/html');
              const newCartItems = doc.querySelector('.js-contents') || doc.querySelector('.js-drawer-inner');
              if (newCartItems) {
                cartItemsContainer.innerHTML = newCartItems.innerHTML;
              }
            }
          } else {
            sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
          }
        }
      });
      
      // EPL-specific: After sections are updated, if we were showing loading screen, restore it
      // (in case section update overwrote it)
      if (wasEmpty) {
        const $drawerItems = this.querySelector('cart-drawer-items-epl');
        if ($drawerItems) {
          const $inner = $drawerItems.querySelector('.js-drawer-inner');
          const $items = $inner?.querySelector('.js-cart-items-body');
          // If loading screen was overwritten or doesn't exist, restore it
          if (!$items || !$items.querySelector('.epl-cart-summary') || 
              !$items.querySelector('.epl-cart-summary').textContent.includes('Laster handlekurv')) {
            if ($inner) {
              $inner.innerHTML = `
                <div class="drawer__cart-items-wrapper js-cart-items-body">
                  <div class="epl-cart-summary" style="min-height: 200px;">
                    <div style="text-align: center; padding: 40px; color: #666;">
                      <p>Laster handlekurv...</p>
                    </div>
                  </div>
                </div>
              `;
            }
          }
        }
      }

      setTimeout(() => {
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
        
        // EPL-specific: For EPL drawer, if we were transitioning from empty, call updateItems to fetch cart content
        if (wasEmpty) {
          // Extract the latest item key from parsedState if available
          const latestItemKey = parsedState.items && parsedState.items.length > 0 
            ? parsedState.items[parsedState.items.length - 1].key 
            : null;
          // Call updateItems to fetch and display the cart content
          this.updateItems(parsedState.items || null, latestItemKey, false);
        }
        
        this.open();
      });
    }

    prepareLoadingScreen(optimisticItem = null) {
      // Hide the empty drawer state immediately
      const $emptyDrawer = this.querySelector('.js-empty-drawer');
      if ($emptyDrawer) {
        $emptyDrawer.classList.add('hidden');
      }

      // Show the cart-drawer-items section and remove is-empty class
      const $drawerItems = this.querySelector('cart-drawer-items-epl');
      if ($drawerItems) {
        $drawerItems.classList.remove('is-empty');
        // Remove is-empty from cart-drawer element itself to prevent CSS rules from affecting layout
        this.classList.remove('is-empty');
        const $inner = $drawerItems.querySelector('.js-drawer-inner');
        if ($inner) {
          // Optimistic card (instant item) when we have click-time data, else a spinner.
          const summaryInner = optimisticItem
            ? this._buildOptimisticCard(optimisticItem)
            : `<div style="text-align: center; padding: 40px; color: #666;">
                  <p>Laster handlekurv...</p>
                </div>`;
          // Insert into the same .js-cart-items-body wrapper the reconcile step replaces.
          $inner.innerHTML = `
            <div class="drawer__cart-items-wrapper js-cart-items-body">
              <div class="epl-cart-summary" style="min-height: 200px;">
                ${summaryInner}
              </div>
            </div>
          `;
        }
      }
    }

    /** Minimal HTML escaper for optimistic-card text (product data is trusted, but escape anyway). */
    _escapeHtml(str) {
      return String(str == null ? '' : str)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    /**
     * Builds the optimistic "Varen er lagt i handlekurven" card shown the instant an ATC is
     * clicked, before /cart/add.js responds. Reuses the real .epl-main-item / .drawer__heading
     * classes so it inherits existing CSS and the reconcile swap (_applyPrerenderedItems
     * overwriting .js-cart-items-body) is seamless. Renders the MAIN product line only; the
     * add-on / upsell rows fill in on reconcile. Falls back gracefully when a field is missing.
     * @param {{title?:string, price?:string, imageUrl?:string}} item
     */
    _buildOptimisticCard(item) {
      const title = this._escapeHtml(item && item.title);
      const price = this._escapeHtml(item && item.price);
      const imageUrl = item && item.imageUrl ? this._escapeHtml(item.imageUrl) : '';
      const img = imageUrl ? `<img src="${imageUrl}" alt="${title}" loading="eager">` : '';
      return `
        <h2 class="drawer__heading">Varen er lagt i handlekurven</h2>
        <div class="epl-main-item epl-main-item--optimistic">
          <div class="epl-main-item__image">${img}</div>
          <div class="epl-main-item__details">
            <p class="epl-main-item__title">${title}</p>
            <p class="epl-main-item__price">${price}</p>
          </div>
        </div>
        <div class="epl-optimistic-pending" aria-hidden="true">
          <span></span><span></span><span></span>
        </div>
      `;
    }

    updateItems(newItems = null, preserveKey = null, shouldOpen = true, retryCount = 0, targetHandle = null, prerenderedHtml = null) {
      // Prevent infinite retry loops
      if (retryCount > 2) {
        console.error('Cart drawer update failed after multiple retries');
        this._enableAllButtons();
        this._setAddPending(false);
        return;
      }

      // Prevent concurrent updateItems calls - queue the latest request
      if (this._updateLock) {
        this._pendingUpdate = { newItems, preserveKey, shouldOpen, retryCount, targetHandle, prerenderedHtml };
        return;
      }
      this._updateLock = true;

      const viewName = 'cart-drawer-epl-items';

      // Helper to check if an item is a main product (not AppleCare, Secondary Warranty, or GWP)
      const isMainProduct = (item) => {
        if (!item) return false;
        const props = item.properties || {};
        const productType = item.product_type || item.product_type_name || '';
        const productTypeName = item.product_type_name || item.product_type || '';

        return !props._aplcr_main_product_sku &&
               !props._sw_main_product_sku &&
               !props._gwp_main_product_sku &&
               productType !== 'AppleCare' &&
               productType !== 'SecondWarranty' &&
               productTypeName !== 'AppleCare' &&
               productTypeName !== 'SecondWarranty' &&
               item.product_id && // Ensure it has a product_id
               !item.key.includes('applecare') && // Additional safety check
               !item.key.includes('warranty'); // Additional safety check
      };

      // Determine the key of the most recently added main product
      let latestItemKey = preserveKey; // Use preserved key if provided

      if (!latestItemKey && newItems && Array.isArray(newItems) && newItems.length > 0) {
        let mainProduct = null;

        // Priority 1: Match by handle if provided (most reliable for re-adds)
        if (targetHandle) {
          mainProduct = newItems.find(item => {
            const handle = item.handle || item.product_handle;
            return handle === targetHandle && isMainProduct(item);
          });
        }

        // Fallback: First main product in array (existing logic)
        if (!mainProduct) {
          mainProduct = newItems.find(item => isMainProduct(item));
        }

        if (mainProduct) {
          latestItemKey = mainProduct.key;
        } else if (newItems.length > 0) {
          // Fallback: use the last item in the array (usually the main product is added last)
          latestItemKey = newItems[newItems.length - 1].key;
        }
      }

      // Add cache-busting parameter to ensure fresh cart data
      const cacheBuster = `&_t=${Date.now()}`;
      const url = latestItemKey
        ? `${window.Shopify.routes.root}cart?view=${viewName}&latest_key=${encodeURIComponent(latestItemKey)}${cacheBuster}`
        : `${window.Shopify.routes.root}cart?view=${viewName}${cacheBuster}`;

      // Check if loading screen was already prepared in open() method
      // If cart-drawer doesn't have is-empty but we have a loading screen, it was prepared
      const $drawerItems = this.querySelector('cart-drawer-items-epl');
      const $existingLoadingScreen = $drawerItems?.querySelector('.js-cart-items-body .epl-cart-summary');
      const loadingScreenAlreadyPrepared = !!$existingLoadingScreen;
      
      // Show loading screen when transitioning from empty to non-empty cart
      // Check if empty drawer exists (even if hidden) to detect empty cart transition
      // Only prepare if it wasn't already prepared in open() method
      const $emptyDrawer = this.querySelector('.js-empty-drawer');
      const wasEmpty = this.classList.contains('is-empty') || ($emptyDrawer && !$emptyDrawer.classList.contains('hidden'));
      if (wasEmpty && !loadingScreenAlreadyPrepared) {
        this.prepareLoadingScreen();
      }
      
      // Open drawer immediately if requested, AFTER preparing loading screen
      // This ensures the loading screen is visible as soon as the drawer opens
      // Check if drawer is already open to avoid unnecessary work
      const isAlreadyOpen = this.classList.contains('active');
      if (shouldOpen && !this._isOpening && !isAlreadyOpen) {
        // Mark that we're opening to prevent refresh loop in open() method
        this._isOpening = true;
        this.open();
        this._isOpening = false;
      }
      
      // SINGLE ROUND-TRIP PATH (Scope C): when /cart/add.js?sections= already returned the
      // rendered drawer HTML, apply it directly and skip the second /cart?view= fetch + verify.
      // Falls through to the normal fetch path below if the section was missing/empty.
      // The try/catch mirrors the .finally() guarantee of the fetch path: a synchronous throw
      // here must never strand _updateLock (which would silently kill all later cart updates).
      if (prerenderedHtml) {
        let applied = false;
        try {
          applied = this._applyPrerenderedItems(prerenderedHtml, wasEmpty, shouldOpen);
        } catch (error) {
          console.error('Error applying pre-rendered cart items:', error);
          applied = false;
        }
        if (applied) {
          this._releaseUpdateLock();
          return;
        }
        // else: section was empty/unparseable, or apply threw — fall through to the normal
        // fetch path, which re-renders the same wrapper and releases the lock in .finally().
      }

      // Fetch immediately without setTimeout wrapper for faster response
      fetch(url)
        .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch cart template: ${response.status} ${response.statusText}`);
        }
        return response.text();
      })
      .then((html) => {
          // Parse HTML to extract just the inner content (avoid duplicate wrappers)
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const wrapperFromTemplate = doc.querySelector('.js-cart-items-body');
          const innerContent = wrapperFromTemplate ? wrapperFromTemplate.innerHTML : html;
          const templateMainItem = doc.querySelector('.epl-main-item');
          const templateItemKey = templateMainItem?.getAttribute('data-item-key') || null;

        const $items = this.querySelector('.js-cart-items-body') || null;

        if (wasEmpty) { 
          const $emptyDrawer = this.querySelector('.js-empty-drawer') || null;
          const $drawerItems = this.querySelector('cart-drawer-items-epl') || null;

          if ($drawerItems) { 
            const $inner = $drawerItems.querySelector('.js-drawer-inner'); 

            if ($inner) { 
              // Parse the HTML to get the cart items body
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, 'text/html');
              const wrapperFromTemplate = doc.querySelector('.js-cart-items-body');
              
              // The template returns the wrapper div directly
              // When cart is empty, inner contains empty state, so we replace it with the wrapper
              if (wrapperFromTemplate) {
                // Replace inner content with the wrapper
                $inner.innerHTML = wrapperFromTemplate.outerHTML;
              } else {
                // Fallback: use the HTML as-is (might be the wrapper itself)
                $inner.innerHTML = html;
              }
              
              const $newItems = $inner.querySelector('.js-cart-items-body') || null;
              
              if ($newItems) { 
                // Update totals (item count and price) from data attributes
                this.updateDrawerTotals($newItems);
                if ($emptyDrawer) {
                  $emptyDrawer.classList.add('hidden');
                }
                $drawerItems.classList.remove('is-empty');
                this.classList.remove('is-empty');

                // Re-initialize handlers after content update
                this._acHandlersInitialized = false;
                this.initAppleCareHandlers();
                this._swHandlersInitialized = false;
                this.initSecondWarrantyHandlers();
                this.initRecommendedAccessoriesHandlers();

                // Re-initialize modal triggers after content update
                if (typeof window.initModalTriggers === 'function') {
                  window.initModalTriggers($drawerItems);
                }

                // Re-enable buttons after DOM update - fresh HTML now has correct button states
                this._enableAllButtons();

                // Mark that we just updated to prevent refresh loop when opening
                this._justUpdated = true;

                if (shouldOpen && !this._isOpening) {
                  this.open();
                }
                
                // Dispatch cart:updated event to notify cart page and other listeners
                fetch(`${window.Shopify.routes.root}cart.js`)
                  .then(response => response.json())
                  .then(cartData => {
                    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartData }));
                  })
                  .catch(error => {
                    console.error('Error fetching cart data for event:', error);
                  });
                
                return; // Exit early since we've handled the empty case
              }
            }
          }
        }

        if ($items) {
          // Use inner content to avoid duplicate wrapper divs
          // Also preserve data attributes from the template wrapper if they exist
          if (wrapperFromTemplate) {
            // Copy data attributes from template wrapper to existing wrapper
            Array.from(wrapperFromTemplate.attributes).forEach(attr => {
              if (attr.name.startsWith('data-')) {
                $items.setAttribute(attr.name, attr.value);
              }
            });
          }
          
          // Check if innerContent is actually populated
          if (!innerContent || innerContent.trim().length === 0) {
            // Check if cart is actually empty by fetching cart.js
            fetch(`${window.Shopify.routes.root}cart.js`)
              .then(response => response.json())
              .then(cart => {
                if (!cart.items || cart.items.length === 0) {
                  // Cart is actually empty, mark drawer as empty
                  this.classList.add('is-empty');
                  const $drawerItems = this.querySelector('cart-drawer-items-epl');
                  if ($drawerItems) {
                    $drawerItems.classList.add('is-empty');
                  }
                  const $emptyDrawer = this.querySelector('.js-empty-drawer');
                  if ($emptyDrawer) {
                    $emptyDrawer.classList.remove('hidden');
                  }
                  return;
                }
                
                // Cart has items but template returned empty - retry after delay
                setTimeout(() => {
                  this.updateItems(null, preserveKey, shouldOpen, retryCount + 1);
                }, 500);
              })
              .catch(error => {
                console.error('Error checking cart state:', error);
                // Retry update as fallback
                setTimeout(() => {
                  this.updateItems(null, preserveKey, shouldOpen, retryCount + 1);
                }, 500);
              });
            return;
          }

          // Check if we should skip replacing HTML to prevent flickering
          // When preserveKey or latestItemKey is provided and template returned wrong product, don't replace
          // Compare by handle (not key) because key changes when add-on properties are updated
          const currentMainItem = $items.querySelector('.epl-main-item');
          const currentDisplayedHandle = currentMainItem?.getAttribute('data-product-handle');
          const templateHandle = wrapperFromTemplate?.querySelector('.epl-main-item')?.getAttribute('data-product-handle');

          if (preserveKey || latestItemKey) {
            // Fetch cart to verify correct product before updating DOM
            const keyToFind = preserveKey || latestItemKey;
            fetch(`${window.Shopify.routes.root}cart.js`)
              .then(response => response.json())
              .then(async (cart) => {
                const correctItem = cart.items.find(item => item.key === keyToFind);
                if (!correctItem) {
                  $items.innerHTML = innerContent;
                  this._finalizeUpdate($items, cart);
                  return;
                }

                const correctHandle = correctItem.handle;
                const templateReturnedWrongProduct = templateHandle && correctHandle && templateHandle !== correctHandle;
                const currentDisplayIsCorrect = currentDisplayedHandle && currentDisplayedHandle === correctHandle;

                if (templateReturnedWrongProduct && currentDisplayIsCorrect) {
                  // Template returned wrong product but correct product already displayed
                  currentMainItem.setAttribute('data-item-key', correctItem.key);
                  await this._updateAddonsForProduct(correctItem, $items);
                } else if (templateReturnedWrongProduct) {
                  // Template returned wrong product — fix everything on detached template DOM
                  // before inserting, so the user never sees wrong content
                  const templateMainItemEl = wrapperFromTemplate?.querySelector('.epl-main-item');
                  if (templateMainItemEl) {
                    this._fixMainItemDisplay(correctItem, templateMainItemEl);
                    // Populate correct upsells on detached DOM (loading screen stays up)
                    await this._updateAddonsForProduct(correctItem, wrapperFromTemplate);
                    $items.innerHTML = wrapperFromTemplate.innerHTML;
                  } else {
                    $items.innerHTML = innerContent;
                  }
                } else {
                  // Template returned correct product — use it directly
                  $items.innerHTML = innerContent;
                }

                this._finalizeUpdate($items, cart);
              })
              .catch(error => {
                console.error('Error checking product match:', error);
                $items.innerHTML = innerContent;
                this._finalizeUpdate($items, null);
              });
            return;
          }

          $items.innerHTML = innerContent;

          // Constrain cart-drawer-items-epl to available height after content update
          const drawerItemsEpl = this.querySelector('cart-drawer-items-epl');
          const drawerInner = this.querySelector('.drawer__inner');
          const drawerHeader = this.querySelector('.drawer__header');
          const drawerFooter = this.querySelector('.drawer__footer');
          
          if (drawerInner && drawerItemsEpl) {
            const innerHeight = parseFloat(getComputedStyle(drawerInner).height) || 0;
            const headerHeight = drawerHeader ? (parseFloat(getComputedStyle(drawerHeader).height) || 0) : 0;
            const footerHeight = drawerFooter ? (parseFloat(getComputedStyle(drawerFooter).height) || 0) : 0;
            const availableHeight = innerHeight - headerHeight - footerHeight;
            
            if (availableHeight > 0) {
              drawerItemsEpl.style.maxHeight = `${availableHeight}px`;
              drawerItemsEpl.style.height = `${availableHeight}px`;
              drawerItemsEpl.style.overflowY = 'auto';
            }
          }
          
          // Update totals (item count and price) from data attributes
          this.updateDrawerTotals($items);
          
          // Mark that we just updated to prevent refresh loop when opening
          this._justUpdated = true;
          
          // Re-initialize AC+ event listeners after content update
          // Reset the flag to allow re-initialization with new content
          this._acHandlersInitialized = false;
          this.initAppleCareHandlers();

          // Re-initialize SW handlers after content update
          this._swHandlersInitialized = false;
          this.initSecondWarrantyHandlers();

          // Re-initialize recommended accessories handlers
          this.initRecommendedAccessoriesHandlers();

          // Re-initialize modal triggers after content update
          if (typeof window.initModalTriggers === 'function') {
            window.initModalTriggers($items);
          }

          // Re-enable buttons after DOM update - fresh HTML now has correct button states
          this._enableAllButtons();

          const mainItem = $items.querySelector('.epl-main-item');

          // If we have a latest item key, verify and fix the displayed item
          // Also dispatches cart:updated event from the same fetch to avoid redundant requests
          let cartEventDispatched = false;
          if (latestItemKey) {
            const displayedItemKey = mainItem ? mainItem.getAttribute('data-item-key') : null;

            // If the displayed item doesn't match, fetch cart JSON and update
            // ALWAYS fix the display when preserveKey is provided to ensure correct item is shown
            if (displayedItemKey !== latestItemKey || preserveKey) {
              cartEventDispatched = true;

              // Fetch cart JSON to get the correct item data
              fetch(`${window.Shopify.routes.root}cart.js`)
                .then(response => response.json())
                .then(cart => {
                  // Find the item with the matching key
                  const correctItem = cart.items.find(item => item.key === latestItemKey);

                  if (correctItem && mainItem) {

                    // Update the image
                    const img = mainItem.querySelector('.epl-main-item__image img');
                    if (img && correctItem.image) {
                      img.src = correctItem.image;
                      img.alt = correctItem.image_alt || correctItem.title;
                    }

                    // Update the title using product_title from cart.js (always correct for the target item)
                    const titleEl = mainItem.querySelector('.epl-main-item__title');
                    if (titleEl) {
                      const displayTitle = correctItem.product_title || correctItem.title;
                      if (displayTitle) {
                        mainItem.setAttribute('data-product-title', displayTitle);
                        titleEl.textContent = displayTitle;
                      }
                    }

                    // Update the price (unit price; cart.js returns prices in cents)
                    const priceEl = mainItem.querySelector('.epl-main-item__price');
                    if (priceEl) {
                      const priceInCents = correctItem.final_price || correctItem.price || 0;
                      const price = priceInCents / 100;
                      const formattedPrice = new Intl.NumberFormat('nb-NO', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      }).format(price);
                      priceEl.textContent = `${formattedPrice} kr`;
                    }

                    // Update variant title and quantity in meta
                    const metaEl = mainItem.querySelector('.epl-main-item__meta');
                    if (metaEl) {
                      let metaText = '';
                      if (correctItem.variant_title && correctItem.variant_title !== 'Default Title') {
                        metaText = correctItem.variant_title;
                      }
                      if (correctItem.quantity && correctItem.quantity > 1) {
                        if (metaText) metaText += ' · ';
                        metaText += `Qty ${correctItem.quantity}`;
                      }
                      metaEl.textContent = metaText;
                    }

                    // Update the item key attribute to reflect the correct item
                    mainItem.setAttribute('data-item-key', correctItem.key);

                    // If template rendered wrong product, fetch and update add-on sections
                    // This handles AC+, SW, Vegglader, and complementary products
                    if (displayedItemKey !== latestItemKey && correctItem.handle) {
                      this._updateAddonsForProduct(correctItem, $items);
                    }
                  }

                  // Dispatch cart:updated event with already-fetched data
                  document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
                })
                .catch(error => {
                  console.error('Error fetching cart JSON:', error);
                });
            }
          }

          // Only open the drawer if explicitly requested, not already opening, and not already open
          const isAlreadyOpen = this.classList.contains('active');
          if (shouldOpen && !this._isOpening && !isAlreadyOpen) {
            this.open();
          }

          // Dispatch cart:updated event if not already handled by the fix-display fetch above
          if (!cartEventDispatched) {
            fetch(`${window.Shopify.routes.root}cart.js`)
              .then(response => response.json())
              .then(cartData => {
                document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartData }));
              })
              .catch(error => {
                console.error('Error fetching cart data for event:', error);
              });
          }
        } else {
          // If $items doesn't exist and cart is not empty, try to refresh by fetching cart again
          if (!wasEmpty) {
            // Force a full refresh by clearing and re-fetching
            setTimeout(() => {
              this.updateItems(null, null, false);
            }, 500);
          }
        }
      })
      .catch((error) => {
        console.error('Error updating cart drawer:', error);
        // If update fails, try to refresh the cart drawer after a delay (with retryCount to prevent infinite loops)
        if (retryCount < 2) {
          setTimeout(() => {
            this.updateItems(null, null, false, retryCount + 1);
          }, 1000);
        }
      })
      .finally(() => {
        // Release lock and process any pending update
        this._releaseUpdateLock();
      });
    }

    addEvents() { 
      const $modalTriggers = this.querySelectorAll('.ac-modal-trigger') || [];

      if ($modalTriggers?.length > 0) { 
        [...$modalTriggers].forEach(($trigger) => {
          $trigger.addEventListener('click', (event) => {
            this.close();
          });
        });
      }
      
      // Initialize AC+ handlers
      this.initAppleCareHandlers();
      
      // Initialize SW handlers
      this.initSecondWarrantyHandlers();
      
      // Initialize recommended accessories handlers
      this.initRecommendedAccessoriesHandlers();
    }
    
    initAppleCareHandlers() {
      // Use event delegation on the cart drawer container so handlers persist after HTML updates
      const container = this.querySelector('.js-cart-items-body') || this;
      
      // Remove old listener if it exists (we'll store a reference to it)
      if (this._acClickHandler) {
        container.removeEventListener('click', this._acClickHandler);
      }
      
      // Create a new handler function and store it
      this._acClickHandler = (e) => {
        const addButton = e.target.closest('.add-cart-apple-care');
        if (addButton) {
          e.preventDefault();
          e.stopPropagation();
          this.handleAddAppleCare(addButton);
          return;
        }
        
        // Handle remove AC+ button
        const removeButton = e.target.closest('.epl-upsell__remove-button[data-action="remove"]');
        if (removeButton) {
          e.preventDefault();
          e.stopPropagation();
          this.handleRemoveAppleCare(removeButton);
          return;
        }
      };
      
      // Attach the handler
      container.addEventListener('click', this._acClickHandler);
      this._acHandlersInitialized = true;
    }
    
    async handleAddAppleCare(button) {
      return this._withOperationLock(button, async () => {
        // Extract data attributes
        const variantId = button.dataset.variantid;
        const mainProductSku = button.dataset.skuparent;
        const mainProductHandle = button.dataset.handleparent;
        const mainProductKey = button.dataset.keyparent;
        const uuid = button.dataset.uuid || mainProductHandle;
        const acSku = button.dataset.skuaplcr;
        const acHandle = button.dataset.handleaplcr;

        // Get current cart to find the main product
        const cart = await this._getCart();

        const mainProductItem = cart.items.find(item => item.key === mainProductKey);
        if (!mainProductItem) {
          throw new Error('Main product not found in cart');
        }

        // Build UUID: append AC+ handle to existing UUID (which may already include SW handle)
        const currentUuid = mainProductItem.properties?._uuid || uuid;
        const mainProductUuid = currentUuid + '-' + acHandle;

        // Update main product with AC+ properties and get the response to capture updated key
        const changeResult = await this._changeCartItem(mainProductKey, mainProductItem.quantity, {
          ...mainProductItem.properties,
          _applecare_sku: acSku,
          _applecare_handle: acHandle,
          _uuid: mainProductUuid
        });

        // Find the updated main product key from the change response
        let updatedMainProductKey = mainProductKey;
        if (changeResult.items && changeResult.items.length > 0) {
          const updatedMainProduct = changeResult.items.find(item =>
            item.sku === mainProductSku &&
            !item.properties?._aplcr_main_product_sku &&
            !item.properties?._sw_main_product_sku &&
            !item.properties?._gwp_main_product_sku &&
            item.product_type !== 'AppleCare' &&
            item.product_type !== 'SecondWarranty'
          );
          if (updatedMainProduct) {
            updatedMainProductKey = updatedMainProduct.key;
          }
        }

        // If SW is already in cart, update its UUID to match the new main product UUID
        const existingSwItem = changeResult.items?.find(item =>
          item.properties?._sw_main_product_sku === mainProductSku &&
          item.properties?._uuid !== mainProductUuid
        );

        if (existingSwItem) {
          await this._changeCartItem(existingSwItem.key, existingSwItem.quantity, {
            ...existingSwItem.properties,
            _uuid: mainProductUuid
          });
        }

        // Add AC+ as separate line item
        await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: mainProductItem.quantity,
              properties: {
                _aplcr_main_product_sku: mainProductSku,
                _aplcr_main_product_handle: mainProductHandle,
                _uuid: mainProductUuid
              }
            }]
          })
        });

        // Refresh cart drawer, preserving the main product key
        this.updateItems(null, updatedMainProductKey);
      }, {
        loadingText: 'Legger til...',
        originalText: 'Legg til',
        errorMessage: 'Kunne ikke legge til AppleCare+. Prøv igjen.'
      });
    }
    
    async handleRemoveAppleCare(button) {
      return this._withOperationLock(button, async () => {
        const acLineKey = button.dataset.acLineKey;
        const mainProductKey = button.dataset.mainProductKey;
        const mainProductUuid = button.dataset.mainProductUuid;

        // Get current cart
        const cart = await this._getCart();

        // Find AC+ line item
        const acLineItem = cart.items.find(item => item.key === acLineKey);
        if (!acLineItem) {
          // Item doesn't exist - cart may be in inconsistent state, refresh drawer
          this.updateItems(null, mainProductKey);
          return;
        }

        // Remove AC+ line item
        await this._changeCartItem(acLineKey, 0);

        // Update main product to remove AC+ properties
        const mainProductItem = cart.items.find(item => item.key === mainProductKey);
        let updatedMainProductKey = mainProductKey;

        if (mainProductItem) {
          const updatedProperties = { ...mainProductItem.properties };
          const acHandle = updatedProperties._applecare_handle;
          delete updatedProperties._applecare_sku;
          delete updatedProperties._applecare_handle;
          // Restore original UUID (remove AC+ handle suffix)
          if (updatedProperties._uuid && acHandle && updatedProperties._uuid.endsWith('-' + acHandle)) {
            updatedProperties._uuid = updatedProperties._uuid.replace('-' + acHandle, '');
          } else if (updatedProperties._uuid && mainProductUuid) {
            updatedProperties._uuid = mainProductUuid;
          }

          // Update main product and get response to capture updated key
          const changeResult = await this._changeCartItem(mainProductKey, mainProductItem.quantity, updatedProperties);

          // Find the updated main product key from the change response
          if (changeResult.items && changeResult.items.length > 0) {
            const updatedMainProduct = changeResult.items.find(item =>
              item.sku === mainProductItem.sku &&
              !item.properties?._aplcr_main_product_sku &&
              !item.properties?._sw_main_product_sku &&
              !item.properties?._gwp_main_product_sku &&
              item.product_type !== 'AppleCare' &&
              item.product_type !== 'SecondWarranty'
            );
            if (updatedMainProduct) {
              updatedMainProductKey = updatedMainProduct.key;
            }
          }
        }

        // Refresh cart drawer, preserving the main product key
        setTimeout(() => {
          this.updateItems(null, updatedMainProductKey, false);
        }, 300);
      }, {
        errorMessage: 'Kunne ikke fjerne AppleCare+. Prøv igjen.'
      });
    }
    
    async handleAddSecondWarranty(button) {
      return this._withOperationLock(button, async () => {
        // Extract data attributes
        const variantId = button.dataset.variantid;
        const mainProductSku = button.dataset.skuparent;
        const mainProductHandle = button.dataset.handleparent;
        const mainProductKey = button.dataset.keyparent;
        const uuid = button.dataset.uuid || mainProductHandle;
        const swSku = button.dataset.skusw;
        const swHandle = button.dataset.handlesw;

        // Get current cart to find the main product
        const cart = await this._getCart();

        const mainProductItem = cart.items.find(item => item.key === mainProductKey);
        if (!mainProductItem) {
          throw new Error('Main product not found in cart');
        }

        // Build UUID: append SW handle to existing UUID (which may already include AC+ handle)
        const currentUuid = mainProductItem.properties?._uuid || uuid;
        const mainProductUuid = currentUuid + '-' + swHandle;

        // Update main product with SW properties and get the response to capture updated key
        const changeResult = await this._changeCartItem(mainProductKey, mainProductItem.quantity, {
          ...mainProductItem.properties,
          _secWarranty_sku: swSku,
          _secWarranty_handle: swHandle,
          _uuid: mainProductUuid
        });

        // Find the updated main product key from the change response
        let updatedMainProductKey = mainProductKey;
        if (changeResult.items && changeResult.items.length > 0) {
          const updatedMainProduct = changeResult.items.find(item =>
            item.sku === mainProductSku &&
            !item.properties?._aplcr_main_product_sku &&
            !item.properties?._sw_main_product_sku &&
            !item.properties?._gwp_main_product_sku &&
            item.product_type !== 'AppleCare' &&
            item.product_type !== 'SecondWarranty'
          );
          if (updatedMainProduct) {
            updatedMainProductKey = updatedMainProduct.key;
          }
        }

        // If AC+ is already in cart, update its UUID to match the new main product UUID
        const existingAcItem = changeResult.items?.find(item =>
          item.properties?._aplcr_main_product_sku === mainProductSku &&
          item.properties?._uuid !== mainProductUuid
        );

        if (existingAcItem) {
          await this._changeCartItem(existingAcItem.key, existingAcItem.quantity, {
            ...existingAcItem.properties,
            _uuid: mainProductUuid
          });
        }

        // Add SW as separate line item
        const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: mainProductItem.quantity,
              properties: {
                _sw_main_product_sku: mainProductSku,
                _sw_main_product_handle: mainProductHandle,
                _uuid: mainProductUuid
              }
            }]
          })
        });

        if (!addResponse.ok) {
          const errorData = await addResponse.json();
          throw new Error(errorData.message || 'Failed to add Second Warranty to cart');
        }

        const addResult = await addResponse.json();

        // Refresh cart drawer, preserving the main product key
        this.updateItems(addResult.items || null, updatedMainProductKey, true);
      }, {
        loadingText: 'Legger til...',
        originalText: 'Legg til',
        errorMessage: 'Kunne ikke legge til Second Warranty. Prøv igjen.'
      });
    }
    
    async handleRemoveSecondWarranty(button) {
      return this._withOperationLock(button, async () => {
        const swLineKey = button.dataset.swLineKey;
        const mainProductKey = button.dataset.mainProductKey;
        const mainProductUuid = button.dataset.mainProductUuid;

        // Get current cart
        const cart = await this._getCart();

        // Find SW line item
        const swLineItem = cart.items.find(item => item.key === swLineKey);
        if (!swLineItem) {
          // Item doesn't exist - cart may be in inconsistent state, refresh drawer
          this.updateItems(null, mainProductKey);
          return;
        }

        // Remove SW line item
        await this._changeCartItem(swLineKey, 0);

        // Update main product to remove SW properties
        const mainProductItem = cart.items.find(item => item.key === mainProductKey);
        let updatedMainProductKey = mainProductKey;

        if (mainProductItem) {
          const updatedProperties = { ...mainProductItem.properties };
          const swHandle = updatedProperties._secWarranty_handle;
          delete updatedProperties._secWarranty_sku;
          delete updatedProperties._secWarranty_handle;
          // Restore UUID by removing SW handle suffix (but preserve AC+ if present)
          if (updatedProperties._uuid && swHandle) {
            const uuidParts = updatedProperties._uuid.split('-' + swHandle);
            if (uuidParts.length > 1) {
              updatedProperties._uuid = uuidParts[0];
            } else if (mainProductUuid) {
              updatedProperties._uuid = mainProductUuid;
            }
          } else if (updatedProperties._uuid && mainProductUuid) {
            updatedProperties._uuid = mainProductUuid;
          }

          // Update main product and get response to capture updated key
          const changeResult = await this._changeCartItem(mainProductKey, mainProductItem.quantity, updatedProperties);

          // Find the updated main product key from the change response
          if (changeResult.items && changeResult.items.length > 0) {
            const updatedMainProduct = changeResult.items.find(item =>
              item.sku === mainProductItem.sku &&
              !item.properties?._aplcr_main_product_sku &&
              !item.properties?._sw_main_product_sku &&
              !item.properties?._gwp_main_product_sku &&
              item.product_type !== 'AppleCare' &&
              item.product_type !== 'SecondWarranty'
            );
            if (updatedMainProduct) {
              updatedMainProductKey = updatedMainProduct.key;
            }
          }
        }

        // Refresh cart drawer, preserving the main product key
        setTimeout(() => {
          this.updateItems(null, updatedMainProductKey, false);
        }, 300);
      }, {
        errorMessage: 'Kunne ikke fjerne Second Warranty. Prøv igjen.'
      });
    }
    
    /**
     * Swap the Second Warranty line of a hard-bundled main product to another
     * picker entry. The addon is mandatory, so it is replaced, never removed:
     * update the main line's back-links (_secWarranty_sku/_handle) and _uuid
     * chain, remove the old SW line, then add the chosen variant with the
     * same link properties.
     */
    async handleSwapSecondWarranty(button) {
      return this._withOperationLock(button, async () => {
        const variantId = button.dataset.variantid;
        const mainProductSku = button.dataset.skuparent;
        const mainProductKey = button.dataset.keyparent;
        const newSwSku = button.dataset.skusw;
        const newSwHandle = button.dataset.handlesw;

        const cart = await this._getCart();

        const mainProductItem = cart.items.find(item => item.key === mainProductKey);
        if (!mainProductItem) {
          throw new Error('Main product not found in cart');
        }

        const mainUuid = mainProductItem.properties?._uuid || button.dataset.uuid;
        const swLine = cart.items.find(item =>
          item.properties?._sw_main_product_sku === mainProductItem.sku &&
          item.properties?._uuid === mainUuid
        );
        if (!swLine) {
          throw new Error('Second Warranty line not found in cart');
        }
        if (String(swLine.variant_id) === String(variantId)) {
          return;
        }

        const oldHandle = mainProductItem.properties?._secWarranty_handle || swLine.handle;
        // Replace the SW segment inside the uuid chain; an AC+ suffix, if
        // any, keeps its position.
        let newUuid = mainUuid;
        if (oldHandle && newSwHandle && mainUuid && mainUuid.indexOf('-' + oldHandle) !== -1) {
          newUuid = mainUuid.replace('-' + oldHandle, '-' + newSwHandle);
        }

        // 1. Main line: new back-links + uuid (rotates the main line key)
        const changeResult = await this._changeCartItem(mainProductKey, mainProductItem.quantity, {
          ...mainProductItem.properties,
          _secWarranty_sku: newSwSku,
          _secWarranty_handle: newSwHandle,
          _uuid: newUuid
        });

        let updatedMainProductKey = mainProductKey;
        if (changeResult.items && changeResult.items.length > 0) {
          const updatedMainProduct = changeResult.items.find(item =>
            item.sku === mainProductSku &&
            !item.properties?._aplcr_main_product_sku &&
            !item.properties?._sw_main_product_sku &&
            !item.properties?._gwp_main_product_sku &&
            item.product_type !== 'AppleCare' &&
            item.product_type !== 'SecondWarranty'
          );
          if (updatedMainProduct) {
            updatedMainProductKey = updatedMainProduct.key;
          }
        }

        // 2. Keep a sibling AC+ line on the same uuid chain
        const existingAcItem = changeResult.items?.find(item =>
          item.properties?._aplcr_main_product_sku === mainProductSku &&
          item.properties?._uuid !== newUuid
        );
        if (existingAcItem) {
          await this._changeCartItem(existingAcItem.key, existingAcItem.quantity, {
            ...existingAcItem.properties,
            _uuid: newUuid
          });
        }

        // 3. Old SW line out (its key is untouched by the main-line change)
        await this._changeCartItem(swLine.key, 0);

        // 4. Chosen variant in, same link properties
        const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: swLine.quantity,
              properties: {
                ...swLine.properties,
                _uuid: newUuid
              }
            }]
          })
        });

        if (!addResponse.ok) {
          const errorData = await addResponse.json();
          throw new Error(errorData.message || 'Failed to swap Second Warranty');
        }

        const addResult = await addResponse.json();

        // Refresh cart drawer, preserving the main product key
        this.updateItems(addResult.items || null, updatedMainProductKey, true);
      }, {
        errorMessage: 'Kunne ikke endre type. Prøv igjen.'
      });
    }

    async handleRemoveVegglader(button) {
      return this._withOperationLock(button, async () => {
        const veggladerLineKey = button.dataset.veggladerLineKey;
        const mainProductKey = button.dataset.mainProductKey;

        // Get current cart
        const cart = await this._getCart();

        // Find Vegglader line item
        const veggladerLineItem = cart.items.find(item => item.key === veggladerLineKey);
        if (!veggladerLineItem) {
          // Item doesn't exist - cart may be in inconsistent state, refresh drawer
          this.updateItems(null, mainProductKey);
          return;
        }

        // Remove Vegglader line item
        await this._changeCartItem(veggladerLineKey, 0);

        // Refresh cart drawer, preserving the main product key
        setTimeout(() => {
          this.updateItems(null, mainProductKey, false);
        }, 300);
      }, {
        errorMessage: 'Kunne ikke fjerne Vegglader. Prøv igjen.'
      });
    }
    
    initSecondWarrantyHandlers() {
      // Use event delegation on the cart drawer container so handlers persist after HTML updates
      const container = this.querySelector('.js-cart-items-body') || this;
      
      // Remove old listener if it exists (we'll store a reference to it)
      if (this._swClickHandler) {
        container.removeEventListener('click', this._swClickHandler);
      }
      
      // Create a new handler function and store it
      this._swClickHandler = (e) => {
        const addButton = e.target.closest('.add-cart-second-warranty');
        if (addButton) {
          e.preventDefault();
          e.stopPropagation();
          this.handleAddSecondWarranty(addButton);
          return;
        }
        
        // Handle remove SW button
        const removeButton = e.target.closest('.epl-upsell__remove-button[data-action="remove-sw"]');
        if (removeButton) {
          e.preventDefault();
          e.stopPropagation();
          this.handleRemoveSecondWarranty(removeButton);
          return;
        }
      };
      
      // Attach the handler
      container.addEventListener('click', this._swClickHandler);
      this._swHandlersInitialized = true;
    }
    
    initRecommendedAccessoriesHandlers() {
      // Always use 'this' (cart-drawer element) for event delegation
      // This ensures clicks work in both empty cart (.js-empty-drawer bestsellers) 
      // and non-empty cart (.js-cart-items-body recommended accessories) sections
      const container = this;
      
      // Remove old listener from the container it was attached to
      if (this._recommendedAccessoriesClickHandler && this._recommendedAccessoriesContainer) {
        this._recommendedAccessoriesContainer.removeEventListener('click', this._recommendedAccessoriesClickHandler);
      }
      
      // Store reference to container for proper cleanup later
      this._recommendedAccessoriesContainer = container;
      
      // Create a new handler function and store it
      this._recommendedAccessoriesClickHandler = (e) => {
        // Check if clicked element is an upsell button (but not AppleCare or Second Warranty buttons)
        let button = e.target.closest('.epl-upsell__product-button');
        
        // Also check for .slider-form buttons within .epl-recommended section (from card-product-slider)
        if (!button) {
          const sliderFormButton = e.target.closest('.slider-form');
          if (sliderFormButton && sliderFormButton.closest('.epl-recommended')) {
            button = sliderFormButton;
          }
        }
        
        // Also check for .epl-cart-drawer-atc-button buttons (from card-product-cart-drawer)
        if (!button) {
          const cartDrawerButton = e.target.closest('.epl-cart-drawer-atc-button');
          if (cartDrawerButton && cartDrawerButton.closest('.epl-recommended')) {
            button = cartDrawerButton;
          }
        }
        
        // Handle Vegglader remove button
        if (button && button.classList.contains('epl-upsell__remove-button')) {
          const action = button.getAttribute('data-action');
          if (action === 'remove-vegglader') {
            e.preventDefault();
            e.stopPropagation();
            this.handleRemoveVegglader(button);
            return;
          }
          // Skip other remove buttons (AC+ and SW have their own handlers)
          if (action === 'remove' || action === 'remove-sw') {
            return;
          }
        }
        
        // Skip if it's an AppleCare or Second Warranty button (they have their own handlers)
        if (button && (
          button.classList.contains('add-cart-apple-care') ||
          button.classList.contains('add-cart-second-warranty')
        )) {
          return;
        }
        
        // Check if button has variant ID (supports data-variant-id, data-variantId, and data-variantid)
        // HTML data attributes with hyphens are accessible via camelCase in dataset
        let variantId = button?.dataset.variantId || button?.getAttribute('data-variant-id');
        
        // Also check for data-variantid (all lowercase, no hyphen) from slider-form buttons
        if (!variantId && button) {
          variantId = button.getAttribute('data-variantid');
        }
        
        if (button && variantId) {
          e.preventDefault();
          e.stopPropagation();
          
          // For slider-form buttons, ensure they have the expected data attributes for handleAddRecommendedAccessory
          if (button.classList.contains('slider-form')) {
            // Ensure data-variant-id exists (handleAddRecommendedAccessory expects this)
            if (!button.getAttribute('data-variant-id') && variantId) {
              button.setAttribute('data-variant-id', variantId);
            }
            
            // Ensure data-product-handle exists (extract from data-productHandle if needed)
            if (!button.getAttribute('data-product-handle')) {
              const productHandle = button.dataset.productHandle || button.getAttribute('data-productHandle');
              if (productHandle) {
                button.setAttribute('data-product-handle', productHandle);
              }
            }
          }
          
          this.handleAddRecommendedAccessory(button);
          return;
        }
      };
      
      // Attach the handler
      container.addEventListener('click', this._recommendedAccessoriesClickHandler);
      this._recommendedAccessoriesHandlersInitialized = true;
    }
    
    /**
     * Drains the next operation from _pendingOperations queue.
     * Handles both queue formats:
     * - { button, operation, options } from _withOperationLock
     * - function from handleAddRecommendedAccessory
     * Skips entries whose buttons are disconnected from DOM.
     */
    _drainNextOperation() {
      while (this._pendingOperations.length > 0) {
        const next = this._pendingOperations.shift();

        // Function format (from handleAddRecommendedAccessory)
        if (typeof next === 'function') {
          setTimeout(() => next(), 100);
          return;
        }

        // Object format (from _withOperationLock) — skip if button detached
        if (next.button && next.button.isConnected) {
          setTimeout(() => this._withOperationLock(next.button, next.operation, next.options), 100);
          return;
        }
        // Button disconnected, loop to try next queued item
      }
    }

    /**
     * Wraps an async operation with lock management, button state handling, and queue processing.
     * @param {HTMLElement} button - The button that triggered the operation
     * @param {Function} operation - Async function containing the operation logic
     * @param {Object} options - Configuration options
     * @param {string} options.loadingText - Text to show while loading (for add operations)
     * @param {string} options.originalText - Original button text to restore on error
     * @param {string} options.errorMessage - Alert message on failure
     * @param {Function} options.queuedRetry - Function to call when queuing (defaults to re-calling with same button)
     */
    async _withOperationLock(button, operation, options = {}) {
      const {
        loadingText = null,
        originalText = button.textContent,
        errorMessage = 'Operasjonen feilet. Prøv igjen.'
      } = options;

      // Check if another operation is in progress
      if (this._operationLock) {
        button.disabled = true;
        // Queue the complete operation with its context (max 5 to prevent unbounded growth)
        if (this._pendingOperations.length < 5) {
          this._pendingOperations.push({
            button,
            operation,
            options: { loadingText, originalText, errorMessage }
          });
        }
        return;
      }

      // Acquire lock
      this._operationLock = true;

      // Disable all buttons to prevent concurrent clicks
      this._disableAllButtons();

      // Disable button and optionally show loading state
      button.disabled = true;
      if (loadingText) {
        button.textContent = loadingText;
      }

      try {
        await operation();
      } catch (error) {
        console.error('Operation failed:', error);
        button.disabled = false;
        button.textContent = originalText;
        alert(errorMessage);
      } finally {
        // Release lock and process next operation
        this._operationLock = false;
        // Don't call _enableAllButtons() here - it will be called after updateItems() completes
        // and the DOM has been updated with fresh HTML showing correct button states
        this._drainNextOperation();
      }
    }

    /**
     * Fetches the current cart state.
     * @returns {Promise<Object>} Parsed cart JSON
     */
    async _getCart() {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`);
      if (!response.ok) {
        throw new Error(`Failed to fetch cart: ${response.status}`);
      }
      return response.json();
    }

    /**
     * Update a cart line item by key (stable) instead of position (fragile).
     * @param {string} itemKey - The cart item key
     * @param {number} quantity - New quantity (0 to remove)
     * @param {Object} [properties] - Optional updated properties
     * @returns {Promise<Object>} Parsed JSON response
     */
    async _changeCartItem(itemKey, quantity, properties = null) {
      const body = { id: itemKey, quantity };
      if (properties !== null) body.properties = properties;
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Cart change failed: ${response.status} - ${errorText}`);
      }
      return response.json();
    }

    /**
     * Fetches add-on data for a product and updates the cart drawer add-on sections.
     * Called when the Liquid template returned wrong product due to query param limitation.
     * @param {Object} correctItem - The correct cart item from cart.js
     * @param {HTMLElement} container - The cart items container (.js-cart-items-body)
     */
    async _updateAddonsForProduct(correctItem, container) {
      try {
        const response = await fetch(`${window.Shopify.routes.root}products/${correctItem.handle}?view=cart-drawer-addons`);
        if (!response.ok) {
          console.error('Failed to fetch add-on data:', response.status);
          return;
        }

        // Parse response as text first to check if it's valid JSON
        const responseText = await response.text();
        let addons;
        try {
          addons = JSON.parse(responseText);
        } catch (parseError) {
          // Response is not valid JSON (likely HTML error page)
          console.error('Add-on endpoint returned invalid JSON');
          return;
        }

        // Get current cart to check what's already added
        const cart = await this._getCart();

        const mainProductSku = correctItem.sku;
        const mainProductHandle = correctItem.handle;
        const mainProductKey = correctItem.key;
        const mainProductUuid = correctItem.properties?._uuid || correctItem.handle;

        // ACE (AppleCare Enterprise) hard-bundle detection
        const isAceHardBundle = correctItem.properties?._applecare_hard_bundle === 'true';

        // Check if AC+ is already in cart for this product
        const cartCareObj = (addons.care_product || isAceHardBundle) ? cart.items.find(item =>
          item.properties?._aplcr_main_product_sku === mainProductSku &&
          item.properties?._uuid === mainProductUuid
        ) : null;

        // SW hard-bundle detection
        const isSwHardBundle = correctItem.properties?._secWarranty_hard_bundle === 'true';

        // Check if SW is already in cart for this product
        const cartSwObj = (addons.sw_product || isSwHardBundle) ? cart.items.find(item =>
          item.properties?._sw_main_product_sku === mainProductSku &&
          item.properties?._uuid === mainProductUuid
        ) : null;

        // Check if Vegglader is already in cart for this product
        const cartVeggladerObj = addons.vegglader_product ? cart.items.find(item =>
          item.variant_id === addons.vegglader_product.variant_id &&
          item.properties?._vegglader_main_product_sku === mainProductSku &&
          item.properties?._vegglader_main_product_handle === mainProductHandle
        ) : null;

        // Update the upsell grid
        const upsellGrid = container.querySelector('.epl-upsell-grid');
        if (!upsellGrid) return;

        // Determine if we should show "Essensielt tilbehør" heading
        const hasAddons = addons.care_product || addons.sw_product ||
          (addons.vegglader_product && correctItem.final_price > 100000) ||
          (isAceHardBundle && cartCareObj) ||
          (isSwHardBundle && cartSwObj);

        // Update or create the heading
        let heading = container.querySelector('.epl-recommended__heading');
        if (hasAddons && !heading) {
          heading = document.createElement('h3');
          heading.className = 'epl-recommended__heading';
          heading.textContent = 'Essensielt tilbehør';
          upsellGrid.parentNode.insertBefore(heading, upsellGrid);
        } else if (!hasAddons && heading) {
          heading.remove();
        }

        // Clear existing upsell content
        upsellGrid.innerHTML = '';

        // Render Vegglader if available and price threshold met
        if (addons.vegglader_product && correctItem.final_price > 100000) {
          const veggladerHtml = this._renderVeggladerUpsell(addons.vegglader_product, {
            mainProductSku,
            mainProductHandle,
            mainProductKey,
            mainProductUuid,
            cartVeggladerObj
          });
          upsellGrid.insertAdjacentHTML('beforeend', veggladerHtml);
        }

        // ACE hard-bundle: render locked row instead of regular AC+
        if (isAceHardBundle && cartCareObj) {
          const aceHtml = this._renderAceHardBundleUpsell(cartCareObj, correctItem);
          upsellGrid.insertAdjacentHTML('beforeend', aceHtml);
        } else if (addons.care_product) {
          // Determine layout: AC+ is wide when SW is not available
          const acIsWide = addons.care_product && !addons.sw_product;
          const acHtml = this._renderAppleCareUpsell(addons.care_product, {
            mainProductSku,
            mainProductHandle,
            mainProductKey,
            mainProductUuid,
            cartCareObj,
            isWide: acIsWide
          });
          upsellGrid.insertAdjacentHTML('beforeend', acHtml);
        }

        // SW hard-bundle: render locked row instead of regular SW
        if (isSwHardBundle && cartSwObj) {
          const swHbHtml = this._renderSwHardBundleUpsell(cartSwObj, correctItem);
          upsellGrid.insertAdjacentHTML('beforeend', swHbHtml);
        } else if (addons.sw_product) {
          const swHtml = this._renderSecondWarrantyUpsell(addons.sw_product, {
            mainProductSku,
            mainProductHandle,
            mainProductKey,
            mainProductUuid,
            cartSwObj
          });
          upsellGrid.insertAdjacentHTML('beforeend', swHtml);
        }

        // Update complementary products section
        this._updateComplementaryProducts(addons.complementary_products, container, addons.vegglader_product);

      } catch (error) {
        console.error('Error updating add-ons:', error);
      }
    }

    /**
     * Renders Vegglader upsell HTML
     */
    _renderVeggladerUpsell(vegglader, context) {
      const { mainProductSku, mainProductHandle, mainProductKey, mainProductUuid, cartVeggladerObj } = context;

      const buttonHtml = cartVeggladerObj
        ? `<button type="button" class="epl-upsell__product-button epl-upsell__remove-button"
             data-action="remove-vegglader"
             data-vegglader-line-key="${cartVeggladerObj.key}"
             data-main-product-key="${mainProductKey}"
             data-main-product-uuid="${mainProductUuid}">Fjern</button>`
        : `<button type="button" class="epl-upsell__product-button"
             data-variant-id="${vegglader.variant_id}"
             data-product-handle="${vegglader.handle}"
             data-main-product-sku="${mainProductSku}"
             data-main-product-handle="${mainProductHandle}"
             data-main-product-key="${mainProductKey}"
             data-main-product-uuid="${mainProductUuid}">Legg til</button>`;

      return `
        <div class="epl-upsell epl-upsell--wide">
          <div class="epl-upsell__header-title">
            <div class="epl-upsell__label">Kjøp lader</div>
            <p class="epl-upsell__sub-label">Produktet leveres uten lader.</p>
          </div>
          <div class="applecare-products-on-cart epl-upsell">
            <div class="epl-upsell__content">
              <div class="applecare-item-img epl-upsell__image">
                ${vegglader.image
                  ? `<a class="care-img" href="${vegglader.url}">
                       <img src="${vegglader.image}" alt="${this._escapeHtml(vegglader.title)}" class="add-on-image-cart" width="80" height="80" loading="lazy">
                     </a>`
                  : '<div class="placeholder--no-image"></div>'
                }
              </div>
              <div class="epl-upsell__title-price">
                <p class="apple-care-card-title epl-upsell__title-price">
                  <span class="epl-upsell__title">${this._escapeHtml(vegglader.title)}</span>
                  <span class="epl-upsell__price">${vegglader.price_formatted}</span>
                </p>
              </div>
            </div>
            <div class="epl-upsell__button">${buttonHtml}</div>
          </div>
        </div>`;
    }

    /**
     * Renders AppleCare+ upsell HTML
     */
    _renderAppleCareUpsell(care, context) {
      const { mainProductSku, mainProductHandle, mainProductKey, mainProductUuid, cartCareObj, isWide } = context;

      const buttonHtml = cartCareObj
        ? `<button type="button" class="epl-upsell__product-button epl-upsell__remove-button"
             data-action="remove"
             data-ac-line-key="${cartCareObj.key}"
             data-main-product-key="${mainProductKey}"
             data-main-product-uuid="${mainProductUuid}">Fjern</button>`
        : `<button type="button" class="epl-upsell__product-button add-cart-apple-care"
             data-variantid="${care.variant_id}"
             data-skuparent="${mainProductSku}"
             data-handleparent="${mainProductHandle}"
             data-keyparent="${mainProductKey}"
             data-uuid="${mainProductUuid}"
             data-skuaplcr="${care.sku}"
             data-handleaplcr="${care.handle}"
             ${care.mpn ? `data-mpnaplcr="${care.mpn}"` : ''}
             data-reference="aplcr"
             ${!care.available ? 'disabled' : ''}>Legg til</button>`;

      return `
        <div class="epl-upsell${isWide ? ' epl-upsell--wide' : ''}" data-ac-upsell="true">
          <div class="applecare-products-on-cart epl-upsell">
            <div class="epl-upsell__content">
              <div class="applecare-item-img epl-upsell__image">
                <a class="care-img" href="#">
                  <img src="${care.image}" loading="lazy" class="add-on-image-cart" width="80" height="80" alt="AppleCare+">
                </a>
              </div>
              <div class="epl-upsell__title-price">
                <p class="apple-care-card-title epl-upsell__title-price">
                  <span class="epl-upsell__title">${this._escapeHtml(care.title)}</span>
                  <span class="epl-upsell__price">${care.price_formatted}</span>
                </p>
              </div>
            </div>
            <div class="epl-upsell__button">${buttonHtml}</div>
          </div>
        </div>`;
    }

    /**
     * Renders ACE (AppleCare Enterprise) hard-bundle locked row
     */
    _renderAceHardBundleUpsell(cartCareObj, mainItem) {
      const title = this._escapeHtml(mainItem.properties?._applecare_title || cartCareObj.title);
      const image = cartCareObj.image ? cartCareObj.image.replace(/width=\d+/, 'width=160') : '';
      const priceCents = cartCareObj.original_price || cartCareObj.price || 0;
      const price = (priceCents / 100).toLocaleString('no-NO', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' kr';

      return `
        <div class="epl-upsell epl-upsell--wide" data-ac-upsell="true" data-ace-hard-bundle="true">
          <div class="applecare-products-on-cart epl-upsell">
            <div class="epl-upsell__content">
              <div class="applecare-item-img epl-upsell__image">
                ${image ? `<img src="${image}" loading="lazy" class="add-on-image-cart" width="80" height="80" alt="${title}">` : ''}
              </div>
              <div class="epl-upsell__title-price">
                <p class="apple-care-card-title epl-upsell__title-price">
                  <span class="epl-upsell__title">${title}</span>
                  <span class="epl-upsell__price">${price}</span>
                </p>
              </div>
            </div>
            <div class="epl-upsell__button">
              <span class="epl-addon-locked-label">Legges til automatisk</span>
            </div>
          </div>
        </div>`;
    }

    /**
     * Renders SW (Second Warranty) hard-bundle locked row
     */
    _renderSwHardBundleUpsell(cartSwObj, mainItem) {
      const title = this._escapeHtml(cartSwObj.title);
      const image = cartSwObj.image ? cartSwObj.image.replace(/width=\d+/, 'width=160') : '';
      const priceCents = cartSwObj.original_price || cartSwObj.price || 0;
      const price = (priceCents / 100).toLocaleString('no-NO', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' kr';

      // Type swap is only offered when the Liquid-rendered picker modal for
      // this item is still in the DOM (once opened it moves to <body> and
      // survives upsell re-renders). Without the modal there is no entry
      // data client-side, so the row falls back to the locked label only.
      const modalId = mainItem?.key ? 'modal-sw-picker-drawer-' + mainItem.key : null;
      const swapButton = modalId && document.getElementById(modalId)
        ? `<button type="button" class="epl-upsell__product-button ac-modal-trigger"
             data-modal-target="${modalId}"
             data-addon-picker-trigger="true"
             data-sw-hard-bundle="true"
             aria-haspopup="dialog"
             aria-controls="${modalId}"
             aria-expanded="false">Endre type</button>`
        : '';

      return `
        <div class="epl-upsell epl-upsell--wide" data-sw-upsell="true" data-sw-hard-bundle="true">
          <div class="applecare-products-on-cart epl-upsell">
            <div class="epl-upsell__content">
              <div class="applecare-item-img epl-upsell__image">
                ${image ? `<img src="${image}" loading="lazy" class="add-on-image-cart" width="80" height="80" alt="${title}">` : ''}
              </div>
              <div class="epl-upsell__title-price">
                <p class="apple-care-card-title epl-upsell__title-price">
                  <span class="epl-upsell__title">${title}</span>
                  <span class="epl-upsell__price">${price}</span>
                  <span class="epl-hb-auto-label">Legges til automatisk</span>
                </p>
              </div>
            </div>
            <div class="epl-upsell__button">
              ${swapButton}
            </div>
          </div>
        </div>`;
    }

    /**
     * Renders Second Warranty upsell HTML
     */
    _renderSecondWarrantyUpsell(sw, context) {
      const { mainProductSku, mainProductHandle, mainProductKey, mainProductUuid, cartSwObj } = context;

      const buttonHtml = cartSwObj
        ? `<button type="button" class="epl-upsell__product-button epl-upsell__remove-button"
             data-action="remove-sw"
             data-sw-line-key="${cartSwObj.key}"
             data-main-product-key="${mainProductKey}"
             data-main-product-uuid="${mainProductUuid}">Fjern</button>`
        : `<button type="button" class="epl-upsell__product-button add-cart-second-warranty"
             data-variantid="${sw.variant_id}"
             data-skuparent="${mainProductSku}"
             data-handleparent="${mainProductHandle}"
             data-keyparent="${mainProductKey}"
             data-uuid="${mainProductUuid}"
             data-skusw="${sw.sku}"
             data-handlesw="${sw.handle}"
             ${sw.mpn ? `data-mpnsw="${sw.mpn}"` : ''}
             data-reference="sw"
             ${!sw.available ? 'disabled' : ''}>Legg til</button>`;

      return `
        <div class="epl-upsell" data-sw-upsell="true">
          <div class="applecare-products-on-cart epl-upsell">
            <div class="epl-upsell__content">
              <div class="applecare-item-img epl-upsell__image">
                <a class="care-img" href="${sw.cta_link || '#'}">
                  <img src="${sw.image}" loading="lazy" class="add-on-image-cart" width="80" height="80" alt="Second Warranty">
                </a>
              </div>
              <div class="epl-upsell__title-price">
                <p class="apple-care-card-title epl-upsell__title-price">
                  <span class="epl-upsell__title">${this._escapeHtml(sw.title)}</span>
                  <span class="epl-upsell__price">${sw.price_formatted}</span>
                </p>
              </div>
            </div>
            <div class="epl-upsell__button">${buttonHtml}</div>
          </div>
        </div>`;
    }

    /**
     * Updates complementary products section
     * Uses the same HTML structure as card-product-cart-drawer.liquid
     */
    _updateComplementaryProducts(products, container, veggladerProduct) {
      // Remove existing recommended section
      const existingSection = container.querySelector('.epl-recommended');
      if (existingSection) {
        existingSection.remove();
      }

      // Filter out vegglader if present
      const filteredProducts = products?.filter(p =>
        !veggladerProduct || p.id !== veggladerProduct.id
      ) || [];

      if (filteredProducts.length === 0) return;

      // Build recommended products HTML using epl-cart-drawer-card structure
      const productsHtml = filteredProducts.map(product => `
        <div class="epl-recommended__item">
          <div class="epl-cart-drawer-card">
            <a href="${product.url}" class="epl-cart-drawer-card__link">
              <div class="epl-cart-drawer-card__media">
                ${product.image
                  ? `<img src="${product.image}" alt="${this._escapeHtml(product.title)}" class="epl-cart-drawer-card__image" loading="lazy" width="150" height="150">`
                  : `<div class="epl-cart-drawer-card__placeholder"></div>`
                }
              </div>
            </a>
            <div class="epl-cart-drawer-card__content">
              <h3 class="epl-cart-drawer-card__title">
                <a href="${product.url}" class="epl-cart-drawer-card__title-link">
                  ${this._escapeHtml(product.title)}
                </a>
              </h3>
              <div class="epl-cart-drawer-card__price">
                <span class="price-item price-item--regular">${product.price_formatted}</span>
              </div>
            </div>
            <div class="epl-cart-drawer-card__actions">
              <button type="button"
                class="epl-cart-drawer-button epl-cart-drawer-atc-button"
                data-variant-id="${product.variant_id}"
                data-product-handle="${product.handle}"
                data-action="add-to-cart"
                ${!product.available ? 'disabled' : ''}>
                ${product.available ? 'Legg til' : 'Utsolgt'}
              </button>
            </div>
          </div>
        </div>
      `).join('');

      const sectionHtml = `
        <div class="epl-recommended">
          <h3 class="epl-recommended__heading">Passer til produktet</h3>
          <div class="epl-recommended__grid">${productsHtml}</div>
        </div>`;

      // Insert after the upsell grid
      const upsellGrid = container.querySelector('.epl-upsell-grid');
      if (upsellGrid) {
        upsellGrid.insertAdjacentHTML('afterend', sectionHtml);
      }
    }

    /**
     * Escapes HTML special characters to prevent XSS
     */
    _escapeHtml(text) {
      if (!text) return '';
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    /**
     * Fixes the main item display using cart.js data.
     * Called when template returned wrong product and current display is also wrong.
     * @param {Object} correctItem - The correct cart item from cart.js
     * @param {HTMLElement} mainItem - The .epl-main-item element
     */
    _fixMainItemDisplay(correctItem, mainItem) {
      // Update image
      const img = mainItem.querySelector('.epl-main-item__image img');
      if (img && correctItem.image) {
        img.src = correctItem.image;
        img.alt = correctItem.image_alt || correctItem.title;
      }

      // Update title
      const titleEl = mainItem.querySelector('.epl-main-item__title');
      if (titleEl) {
        const displayTitle = correctItem.product_title || correctItem.title;
        if (displayTitle) {
          mainItem.setAttribute('data-product-title', displayTitle);
          titleEl.textContent = displayTitle;
        }
      }

      // Update price
      const priceEl = mainItem.querySelector('.epl-main-item__price');
      if (priceEl) {
        const priceInCents = correctItem.final_price || correctItem.price || 0;
        const price = priceInCents / 100;
        const formattedPrice = new Intl.NumberFormat('nb-NO', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(price);
        priceEl.textContent = `${formattedPrice} kr`;
      }

      // Update meta (variant title and quantity)
      const metaEl = mainItem.querySelector('.epl-main-item__meta');
      if (metaEl) {
        let metaText = '';
        if (correctItem.variant_title && correctItem.variant_title !== 'Default Title') {
          metaText = correctItem.variant_title;
        }
        if (correctItem.quantity && correctItem.quantity > 1) {
          if (metaText) metaText += ' · ';
          metaText += `Qty ${correctItem.quantity}`;
        }
        metaEl.textContent = metaText;
      }

      // Update data attributes
      mainItem.setAttribute('data-item-key', correctItem.key);
      mainItem.setAttribute('data-product-handle', correctItem.handle);
    }

    /**
     * Releases the update lock and drains any queued update. Shared by the normal
     * fetch path (.finally) and the pre-rendered path so neither leaves a stuck lock.
     */
    _releaseUpdateLock() {
      this._updateLock = false;
      // updateItems completing is the commit point for an in-flight ATC add: clear the
      // pending gate here so it lifts exactly when the cart is updated and shown. Harmless
      // (already false) for non-add updateItems calls.
      this._setAddPending(false);
      if (this._pendingUpdate) {
        const { newItems, preserveKey, shouldOpen, retryCount, targetHandle, prerenderedHtml } = this._pendingUpdate;
        this._pendingUpdate = null;
        // Use setTimeout to avoid stack overflow on rapid successive calls
        setTimeout(() => {
          this.updateItems(newItems, preserveKey, shouldOpen, retryCount, targetHandle, prerenderedHtml);
        }, 50);
      }
    }

    /**
     * Applies drawer-items HTML returned by /cart/add.js bundled section rendering,
     * with no extra network round-trip. Returns true on success, false if the HTML had
     * no usable items body (caller then falls back to the normal /cart?view= fetch).
     *
     * Unlike the fetch path, this deliberately trusts the section HTML and does NOT run the
     * latestItemKey display-correction (image/title/price/addon fixups). That is safe because
     * the section selects the just-added main product via the template's newest-first fallback
     * (cart.items is newest-first), which is verified correct. If a future template change can
     * render the wrong main item, restore a correction step here.
     * @param {string} html - The rendered `cart-drawer-epl-items` section HTML.
     * @param {boolean} wasEmpty - Whether the drawer was empty before this update.
     * @param {boolean} shouldOpen - Whether to open the drawer after applying.
     */
    _applyPrerenderedItems(html, wasEmpty, shouldOpen) {
      window.__eplAtcMark?.('drawer:apply-start');
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const wrapperFromTemplate = doc.querySelector('.js-cart-items-body');
      if (!wrapperFromTemplate) return false;
      const innerContent = wrapperFromTemplate.innerHTML;
      if (!innerContent || innerContent.trim().length === 0) return false;

      if (wasEmpty) {
        // Empty -> non-empty: replace the drawer inner with the rendered wrapper.
        const $emptyDrawer = this.querySelector('.js-empty-drawer');
        const $drawerItems = this.querySelector('cart-drawer-items-epl');
        const $inner = $drawerItems ? $drawerItems.querySelector('.js-drawer-inner') : null;
        if (!$inner) return false;
        $inner.innerHTML = wrapperFromTemplate.outerHTML;
        if ($emptyDrawer) $emptyDrawer.classList.add('hidden');
        if ($drawerItems) $drawerItems.classList.remove('is-empty');
        this.classList.remove('is-empty');
      } else {
        // Already had items: replace the items body, preserving its data-* attributes.
        const $items = this.querySelector('.js-cart-items-body');
        if (!$items) return false;
        Array.from(wrapperFromTemplate.attributes).forEach(attr => {
          if (attr.name.startsWith('data-')) $items.setAttribute(attr.name, attr.value);
        });
        $items.innerHTML = innerContent;
      }

      const $applied = this.querySelector('.js-cart-items-body');

      // Open after applying so the loading screen is replaced by real items in one paint.
      const isAlreadyOpen = this.classList.contains('active');
      if (shouldOpen && !this._isOpening && !isAlreadyOpen) {
        this.open();
      }

      // Re-init add-on/AC/SW/recommended handlers + totals + re-enable buttons, and dispatch
      // cart:updated (passing null => one non-blocking cart.js for external listeners only).
      window.__eplAtcMark?.('drawer:dom-swapped (item now visible)');
      this._finalizeUpdate($applied, null);
      window.__eplAtcMark?.('drawer:finalize-done');
      return true;
    }

    /**
     * Runs all finalization steps after cart drawer content is updated.
     * Handles height constraints, handler re-init, buttons, and cart:updated event.
     * @param {HTMLElement} container - The cart items container (.js-cart-items-body)
     * @param {Object|null} cart - Cart data from cart.js (if available, avoids extra fetch)
     */
    _finalizeUpdate(container, cart) {
      // Constrain cart-drawer-items-epl to available height
      const drawerItemsEpl = this.querySelector('cart-drawer-items-epl');
      const drawerInner = this.querySelector('.drawer__inner');
      const drawerHeader = this.querySelector('.drawer__header');
      const drawerFooter = this.querySelector('.drawer__footer');

      if (drawerInner && drawerItemsEpl) {
        const innerHeight = parseFloat(getComputedStyle(drawerInner).height) || 0;
        const headerHeight = drawerHeader ? (parseFloat(getComputedStyle(drawerHeader).height) || 0) : 0;
        const footerHeight = drawerFooter ? (parseFloat(getComputedStyle(drawerFooter).height) || 0) : 0;
        const availableHeight = innerHeight - headerHeight - footerHeight;

        if (availableHeight > 0) {
          drawerItemsEpl.style.maxHeight = `${availableHeight}px`;
          drawerItemsEpl.style.height = `${availableHeight}px`;
          drawerItemsEpl.style.overflowY = 'auto';
        }
      }

      this._justUpdated = true;
      window.__eplAtcMark?.('drawer:height-reflow-done');
      this._reinitializeHandlers(container);
      window.__eplAtcMark?.('drawer:handlers-reinit-done');

      // Dispatch cart:updated event
      if (cart) {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      } else {
        fetch(`${window.Shopify.routes.root}cart.js`)
          .then(response => response.json())
          .then(cartData => {
            window.__eplAtcMark?.('cart:updated dispatched (trailing non-blocking cart.js)');
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartData }));
          })
          .catch(error => {
            console.error('Error fetching cart data for event:', error);
          });
      }
    }

    /**
     * Re-initializes event handlers after HTML content is replaced.
     * @param {HTMLElement} container - The container with new content
     */
    _reinitializeHandlers(container) {
      this._acHandlersInitialized = false;
      this.initAppleCareHandlers();
      this._swHandlersInitialized = false;
      this.initSecondWarrantyHandlers();
      this.initRecommendedAccessoriesHandlers();
      if (typeof window.initModalTriggers === 'function') {
        window.initModalTriggers(container);
      }
      this.updateDrawerTotals(container);

      // Re-enable buttons after DOM update - fresh HTML now has correct button states
      this._enableAllButtons();
    }

    _disableAllButtons() {
      // Disable all AC+, SW add buttons, remove buttons (including Vegglader), and recommended accessory buttons
      // Don't change button text - just disable them visually
      const container = this.querySelector('.js-cart-items-body') || this;
      const acAddButtons = container.querySelectorAll('.add-cart-apple-care:not(:disabled)');
      const swAddButtons = container.querySelectorAll('.add-cart-second-warranty:not(:disabled)');
      // This selector includes all remove buttons: AC+, SW, and Vegglader (data-action="remove-vegglader")
      const removeButtons = container.querySelectorAll('.epl-upsell__remove-button:not(:disabled)');
      // Recommended accessory buttons (bestsellers and recommended accessories)
      const recommendedButtons = container.querySelectorAll('.epl-cart-drawer-atc-button:not(:disabled), .slider-form:not(:disabled)');
      
      this._disabledButtons = [];
      
      acAddButtons.forEach(btn => {
        if (!btn.disabled) {
          this._disabledButtons.push({ button: btn, originalText: btn.textContent, isAdd: true });
          btn.disabled = true;
          // Don't change text - just disable
        }
      });
      
      swAddButtons.forEach(btn => {
        if (!btn.disabled) {
          this._disabledButtons.push({ button: btn, originalText: btn.textContent, isAdd: true });
          btn.disabled = true;
          // Don't change text - just disable
        }
      });
      
      removeButtons.forEach(btn => {
        if (!btn.disabled) {
          this._disabledButtons.push({ button: btn, originalText: btn.textContent, isAdd: false });
          btn.disabled = true;
          // Don't change text - just disable
        }
      });
      
      recommendedButtons.forEach(btn => {
        if (!btn.disabled) {
          this._disabledButtons.push({ button: btn, originalText: btn.textContent, isAdd: true, isRecommended: true });
          btn.disabled = true;
          // Don't change text - just disable
        }
      });
    }
    
    _enableAllButtons() {
      // If another operation is in progress, don't re-enable — that operation owns button state.
      // This prevents a stale updateItems() fetch from re-enabling buttons mid-operation.
      if (this._operationLock) return;

      // Re-enable all previously disabled buttons
      // For add buttons, DON'T restore text - let updateItems() handle the button state via HTML refresh
      // For remove buttons, the cart update will have already updated them, so we just re-enable
      if (this._disabledButtons) {
        this._disabledButtons.forEach(({ button, originalText, isAdd, isRecommended }) => {
          // Recommended accessory buttons - just re-enable, text will be updated by cart refresh
          if (isRecommended) {
            button.disabled = false;
          }
          // For AC/SW add buttons, don't restore text - the HTML will be refreshed by updateItems()
          // which will show the correct button state (either "Legg til" or "Fjern")
          // This prevents the button from briefly showing "Legg til" before the HTML update completes
          else if (isAdd && (button.classList.contains('add-cart-apple-care') || button.classList.contains('add-cart-second-warranty'))) {
            button.disabled = false;
            // Don't restore textContent - let updateItems() handle it via HTML refresh
          } else if (!isAdd && button.classList.contains('epl-upsell__remove-button')) {
            // Just re-enable remove buttons, text will be updated by cart refresh
            button.disabled = false;
          }
        });
        this._disabledButtons = [];
      }
    }
    
    /**
     * Updates the cart count badge without full drawer refresh
     * Fetches cart.js and updates the cart count bubble in the header
     * Also updates drawer totals if drawer is open
     */
    async updateCartCount() {
      try {
        const cart = await this._getCart();
        
        // Calculate total item count
        const itemCount = cart.items.reduce((total, item) => total + item.quantity, 0);
        
        // Update cart count bubble
        const cartCountBubble = document.querySelector('.cart-count-bubble span');
        const cartIconBubble = document.querySelector('#cart-icon-bubble');
        
        if (cartCountBubble) {
          cartCountBubble.textContent = itemCount;
        } else if (cartIconBubble && itemCount > 0) {
          // Create cart count bubble if it doesn't exist
          const bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          const span = document.createElement('span');
          span.textContent = itemCount;
          bubble.appendChild(span);
          cartIconBubble.appendChild(bubble);
        }
        
        // Also update drawer totals if drawer is open (EPL-specific)
        if (this.classList.contains('active')) {
          const $items = this.querySelector('.js-cart-items-body');
          if ($items) {
            // Update data attributes so updateDrawerTotals can use them
            const priceInCents = cart.total_price || 0;
            const price = priceInCents / 100;
            const formattedPrice = new Intl.NumberFormat('nb-NO', {
              style: 'currency',
              currency: cart.currency || 'NOK',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(price);
            
            $items.setAttribute('data-cart-price', formattedPrice);
            $items.setAttribute('data-cart-item-count', cart.item_count || 0);
            
            // Update the totals display
            this.updateDrawerTotals($items);
          }
        }
      } catch (error) {
        console.error('Error updating cart count:', error);
        // Silently fail - cart count update is not critical
      }
    }
    
    /**
     * Updates the drawer totals (item count and total price) from data attributes
     * Falls back to fetching cart.js if data attributes aren't available
     * @param {HTMLElement} itemsContainer - The container element with data attributes (usually .js-cart-items-body)
     */
    updateDrawerTotals(itemsContainer) {
      const $total = this.querySelector('.js-drawer-total');
      const $itemCount = this.querySelector('.js-drawer-item-count');
      const $eplFooterCount = this.querySelector('.drawer__footer--epl .count');
      
      // Try to get values from data attributes first
      let cartPrice = null;
      let cartItemCount = null;
      
      if (itemsContainer) {
        const dataset = itemsContainer.dataset || {};
        cartPrice = dataset.cartPrice;
        // Check both camelCase (dataset) and direct attribute (for data-cart-item-count)
        cartItemCount = dataset.cartItemCount || itemsContainer.getAttribute('data-cart-item-count');
      }
      
      // If we have cartItemCount, update EPL footer count immediately (even without cartPrice)
      if (cartItemCount && $eplFooterCount) {
        $eplFooterCount.textContent = cartItemCount;
      }
      
      // If we have both values from data attributes, update immediately
      if (cartPrice && cartItemCount) {
        if ($total) {
          $total.textContent = cartPrice;
        }
        if ($itemCount) {
          const count = parseInt(cartItemCount, 10);
          const itemText = count === 1 ? 'vare' : 'varer';
          $itemCount.innerHTML = `${count} ${itemText} i <a href="${window.Shopify.routes.root}cart">handlekurven</a>`;
        }
        // EPL footer count already updated above
        return;
      }
      
      // If we only have cartItemCount (no cartPrice), update what we can and return
      if (cartItemCount && !cartPrice) {
        if ($itemCount) {
          const count = parseInt(cartItemCount, 10);
          const itemText = count === 1 ? 'vare' : 'varer';
          $itemCount.innerHTML = `${count} ${itemText} i <a href="${window.Shopify.routes.root}cart">handlekurven</a>`;
        }
        // EPL footer count already updated above
        return;
      }
      
      // Fallback: fetch from cart.js if data attributes aren't available
      if ((!cartPrice || !cartItemCount) && ($total || $itemCount || $eplFooterCount)) {
        fetch(`${window.Shopify.routes.root}cart.js`)
          .then(response => response.json())
          .then(cart => {
            // Update total price
            if ($total) {
              // Format price using Shopify's money format
              const priceInCents = cart.total_price || 0;
              const price = priceInCents / 100;
              // Use the same format as Shopify (with currency)
              const formattedPrice = new Intl.NumberFormat('nb-NO', {
                style: 'currency',
                currency: cart.currency || 'NOK',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(price);
              $total.textContent = formattedPrice;
            }
            
            // Update item count
            if ($itemCount) {
              const itemCount = cart.item_count || 0;
              const itemText = itemCount === 1 ? 'vare' : 'varer';
              $itemCount.textContent = `${itemCount} ${itemText}`;
            }
            
            // Update EPL footer count
            if ($eplFooterCount) {
              $eplFooterCount.textContent = cart.item_count || 0;
            }
          })
          .catch(error => {
            console.error('Error updating drawer totals:', error);
          });
      }
    }
    
    async handleAddRecommendedAccessory(button) {
      // Check if button is an anchor or button element
      const isAnchor = button.tagName === 'A';
      
      // Check if another operation is in progress
      if (this._operationLock) {
        // Disable button and queue this operation
        if (isAnchor) {
          button.style.pointerEvents = 'none';
          button.style.opacity = '0.6';
        } else {
          button.disabled = true;
        }
        this._pendingOperations.push(() => this.handleAddRecommendedAccessory(button));
        return;
      }
      
      // Acquire lock
      this._operationLock = true;
      
      // Disable all buttons to prevent concurrent clicks
      this._disableAllButtons();
      
      // Disable button and show loading state
      if (isAnchor) {
        button.style.pointerEvents = 'none';
        button.style.opacity = '0.6';
      } else {
        button.disabled = true;
      }
      const originalText = button.textContent;
      button.textContent = 'Legger til...';
      
      try {
        // Extract data attributes (supports data-variant-id, data-variantId, and data-variantid)
        let variantId = button.dataset.variantId || button.getAttribute('data-variant-id');
        if (!variantId) {
          variantId = button.getAttribute('data-variantid');
        }
        
        // Extract product handle (supports data-product-handle, data-productHandle)
        let productHandle = button.dataset.productHandle || button.getAttribute('data-product-handle');
        if (!productHandle) {
          productHandle = button.getAttribute('data-productHandle');
        }
        
        if (!variantId) {
          throw new Error('Variant ID mangler');
        }
        
        // Check if this is a Vegglader button (in the .epl-upsell--wide section)
        const isVegglader = button.closest('.epl-upsell--wide') !== null;
        const mainProductSku = button.dataset.mainProductSku || button.getAttribute('data-main-product-sku');
        const mainProductHandle = button.dataset.mainProductHandle || button.getAttribute('data-main-product-handle');
        const mainProductUuid = button.dataset.mainProductUuid || button.getAttribute('data-main-product-uuid');
        const mainProductKey = button.dataset.mainProductKey || button.getAttribute('data-main-product-key');
        
        // Build properties object
        const properties = {
          _uuid: mainProductUuid || productHandle || '',
          _recommended_accessory: 'true'
        };
        
        // If it's a Vegglader, add main product tracking properties
        if (isVegglader && mainProductSku && mainProductUuid) {
          properties._vegglader_main_product_sku = mainProductSku;
          properties._vegglader_main_product_handle = mainProductHandle || '';
          properties._vegglader_main_product_uuid = mainProductUuid;
          // Use main product UUID instead of productHandle for _uuid
          properties._uuid = mainProductUuid;
        }
        
        // Add accessory to cart
        const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: parseInt(variantId),
              quantity: 1,
              properties: properties
            }]
          })
        });
        
        if (!addResponse.ok) {
          const errorData = await addResponse.json();
          throw new Error(errorData.message || 'Kunne ikke legge til produktet');
        }
        
        const addResult = await addResponse.json();

        // Refresh cart drawer to show remove button (button stays in "Legger til..." state until refresh)
        // Always preserve the current main product to prevent flickering
        const currentMainItem = this.querySelector('.epl-main-item');
        const currentMainProductKey = mainProductKey || (currentMainItem ? currentMainItem.getAttribute('data-item-key') : null);
        if (currentMainProductKey) {
          this.updateItems(null, currentMainProductKey);
        } else {
          // Full refresh to re-render recommended section with correct button states
          this.updateItems();
        }
      } catch (error) {
        console.error('Error adding recommended accessory:', error);
        if (isAnchor) {
          button.style.pointerEvents = '';
          button.style.opacity = '';
          button.style.cursor = '';
        } else {
          button.disabled = false;
        }
        button.textContent = originalText;
        alert('Kunne ikke legge til produktet. Prøv igjen.');
        
        // On error, do a full refresh to ensure cart state is correct
        const mainItem = this.querySelector('.epl-main-item');
        const currentMainProductKey = mainItem ? mainItem.getAttribute('data-item-key') : null;
        if (currentMainProductKey) {
          this.updateItems(null, currentMainProductKey);
        } else {
          this.updateItems();
        }
      } finally {
        // Release lock and process next operation
        // Don't call _enableAllButtons() here - updateItems() will call it after DOM update
        // This prevents buttons from being re-enabled with stale text before fresh HTML arrives
        this._operationLock = false;
        this._drainNextOperation();
      }
    }
  }

  if (!customElements.get('cart-drawer-epl')) {
    customElements.define('cart-drawer-epl', CartDrawerEpl);
  }

})(); // End of IIFE and __EPL_CART_DRAWER_LOADED__ check
